import { useCallback, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";

const KEYS = {
  userId: "superadmin_impersonating_user_id",
  clinicId: "superadmin_impersonating_clinic_id",
  name: "superadmin_impersonating_name",
  role: "superadmin_impersonating_role",
} as const;

let listeners: (() => void)[] = [];
function emitChange() {
  listeners.forEach((l) => l());
}
function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getSnapshot() {
  return sessionStorage.getItem(KEYS.userId);
}

export function useImpersonating() {
  const navigate = useNavigate();
  const targetUserId = useSyncExternalStore(subscribe, getSnapshot);
  const isImpersonating = !!targetUserId;

  const targetName = isImpersonating ? sessionStorage.getItem(KEYS.name) || "Unknown" : null;
  const targetClinicId = isImpersonating ? sessionStorage.getItem(KEYS.clinicId) : null;

  const startImpersonating = useCallback(
    (userId: string, clinicId: string, name: string, targetRole: string) => {
      sessionStorage.setItem(KEYS.userId, userId);
      sessionStorage.setItem(KEYS.clinicId, clinicId);
      sessionStorage.setItem(KEYS.name, name);
      sessionStorage.setItem(KEYS.role, targetRole);
      emitChange();
      navigate(targetRole === "staff" ? "/staff-dashboard" : "/dashboard");
    },
    [navigate]
  );

  const stopImpersonating = useCallback(() => {
    sessionStorage.removeItem(KEYS.userId);
    sessionStorage.removeItem(KEYS.clinicId);
    sessionStorage.removeItem(KEYS.name);
    sessionStorage.removeItem(KEYS.role);
    emitChange();
    navigate("/superadmin");
  }, [navigate]);

  return { isImpersonating, targetUserId, targetClinicId, targetName, startImpersonating, stopImpersonating };
}
